import { makeAutoObservable, runInAction } from 'mobx'

import { serverMessage, serverStatus } from '@/Ship/Helpers/serverError'

import { CompleteActivityTask } from '../Tasks/CompleteActivityTask'

import type { ActivityRecord } from '../Contracts/Activity.types'

/** Текст легасі під полем, коли результату немає (`ShipUk:389`, І12). */
export const COMPLETE_RESULT_REQUIRED = 'Заповніть поле з результатом перед завершенням.'

/** Коли сервер пояснення не дав (`SPEC-011` §8.5, З5). */
export const COMPLETE_FAILED = 'Не вдалося завершити справу.'

/**
 * Статуси, після яких модалка безглузда: 400 — справу вже завершили (хтось
 * раніше), 403 — права змінилися, 404 — справу видалено. Картку тоді
 * перечитуємо, щоб кнопка показала справжній стан.
 */
const STALE_STATUSES = new Set([400, 403, 404])

export type CompleteActivityOutcome =
    | { kind: 'done'; card: ActivityRecord | null }
    | { kind: 'invalid' }
    | { kind: 'failed'; message: string; stale: boolean }

/**
 * Модалка «Завершити справу» (`SPEC-011` §8.5): одне поле «Результат»,
 * обов'язкове.
 *
 *  - відкривається з поточним результатом справи (його могли вписати
 *    раніше, К-15);
 *  - порожній результат — текст легасі під полем, модалка лишається
 *    відкритою, запиту немає (бек однаково відхилив би, І12);
 *  - 422 з бека — його текст під полем, модалка відкрита;
 *  - 400 / 403 / 404 — модалка закривається, картку треба перечитати.
 *
 * Тости й перечитування картки — справа того, хто модалку відкрив
 * (`ActivityViewActions`): стор лише повертає, чим скінчилося.
 */
export class CompleteActivityModalStore {
    open = false
    uuid: string | null = null
    result = ''
    error: string | null = null
    loading = false

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    openFor(record: Pick<ActivityRecord, 'uuid' | 'result'> | null | undefined): void {
        const uuid = record?.uuid

        if (typeof uuid !== 'string' || uuid === '') return

        this.uuid = uuid
        this.result = typeof record?.result === 'string' ? record.result : ''
        this.error = null
        this.loading = false
        this.open = true
    }

    close(): void {
        this.open = false
        this.uuid = null
        this.result = ''
        this.error = null
        this.loading = false
    }

    setResult(value: string): void {
        this.result = value
        this.error = null
    }

    async confirm(): Promise<CompleteActivityOutcome> {
        const uuid = this.uuid

        if (uuid === null || this.loading) return { kind: 'invalid' }

        const result = this.result.trim()

        if (result === '') {
            this.error = COMPLETE_RESULT_REQUIRED
            return { kind: 'invalid' }
        }

        this.loading = true
        this.error = null

        try {
            const card = await CompleteActivityTask(uuid, result)

            runInAction(() => this.close())

            return { kind: 'done', card }
        } catch (error: unknown) {
            const status = serverStatus(error)
            const message = serverMessage(error, COMPLETE_FAILED)
            const stale = status !== null && STALE_STATUSES.has(status)

            runInAction(() => {
                this.loading = false

                if (stale) {
                    this.close()
                    return
                }

                // 422 на «Результаті» й збої зв'язку — під полем: їх можна
                // виправити й надіслати знову, не втрачаючи введеного
                this.error = message
            })

            return { kind: 'failed', message, stale }
        }
    }
}
